import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { Contact } from 'app/core/models';
import { ContactStore } from 'app/core/stores/base.store';
import { FuseConfirmationService } from 'app/core/services/confirmation/confirmation.service';
import { NotificationUtils } from 'app/core/utils';

// Using the contact interface from core/models

@Component({
  selector: 'app-add-contact',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatToolbarModule,
    MatCardModule
  ],
  templateUrl: './add-contact.component.html',
  styleUrls: ['./add-contact.component.css']
})
export class AddContactComponent {
  contactForm: FormGroup;
  isEdit = false;
  contactId: number | null = null;
  
  // Contact categories
  categories = ['Self', 'Borrower', 'Insurance', 'Income'];
  
  states = [
    'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN',
    'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ',
    'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT',
    'WA', 'WI', 'WV', 'WY'
  ];
  
  contactStore = inject(ContactStore);
  #fuseConfirmationService = inject(FuseConfirmationService);


  constructor(
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.contactForm = this.fb.group({
      id: [null],
      user_id: [JSON.parse(localStorage.getItem('user_profile')).id],
      category: ['', Validators.required],
      first_name: [''],
      last_name: [''],
      organization: [''],
    });
  }

  ngOnInit() {
    if (this.contactStore.selected()) {
      this.isEdit = true;
      this.contactForm.patchValue(this.contactStore.selected());
    } 
  }

  onSubmit() {
    if (this.contactForm.valid) {
      const formValue = this.contactForm.value;
      
      // For Self category, only first name and last name are required
      if (formValue.category === 'Self') {
        if (formValue.first_name === "" || formValue.last_name === "") {
          NotificationUtils.getInstance().showError('Both first name and last name are required for Self');
          return;
        }
      } 
      // For Insurance category, both insured name and organization are required
      else if (formValue.category === 'Insurance') {
        if (formValue.first_name === "" || formValue.organization === "") {
          NotificationUtils.getInstance().showError('Insured name and organization is required');
          return;
        }
      }
      // For all other categories, either first name or organization is required
      else if (formValue.first_name === "" && formValue.organization === "") {
        NotificationUtils.getInstance().showError('First name or organization is required');
        return;
      }
      
      if (this.isEdit) {
        this.contactStore.update(formValue)
      } else {
        // For new contacts, we need to create a new contact
        delete formValue.id
        this.contactStore.create({
          entity: formValue,
          endpoint: 'contact'
        });
      }
      this.router.navigate(['/contacts']);
    }
  }

  onDelete(contact:Contact) {
    event.stopPropagation() // Prevent row click
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Contact',
      message: 'Are you sure you want to delete this contact? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.contactStore.delete(contact);
      }
    })
    this.router.navigate(['/contacts']);
  }

  onCancel() {
    this.router.navigate(['/contacts']);
  }
}
